import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  BookOpen,
  ExternalLink,
  FileSignature,
  Loader2,
  ShieldCheck,
} from "lucide-react";

/**
 * Modal/pantalla bloqueante para firmar el Acuerdo de Uso y Consentimiento
 * Informado (Fase 6). Se renderiza sobre toda la app si el usuario está
 * autenticado y no ha firmado la versión vigente.
 */
export default function AcuerdoGate({ onSigned }) {
  const { user, refresh } = useAuth();
  const [state, setState] = useState({
    checking: true,
    needsSign: false,
    version: null,
    fechaVigencia: null,
  });
  const [accepted, setAccepted] = useState(false);
  const [nombre, setNombre] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user || !user.id) {
        if (alive) setState((s) => ({ ...s, checking: false, needsSign: false }));
        return;
      }
      try {
        const [{ data: est }, { data: ver }] = await Promise.all([
          api.get("/acuerdo/estado"),
          api.get("/acuerdo/version-actual"),
        ]);
        if (!alive) return;
        setState({
          checking: false,
          needsSign: !!est.requiere_firma,
          version: ver.version,
          fechaVigencia: ver.fecha_vigencia,
        });
      } catch (e) {
        // Si /estado falla (p.ej. 401), no bloquear render normal — ProtectedRoute
        // se encargará. Log silencioso.
        if (alive) setState({ checking: false, needsSign: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const handleFirmar = async () => {
    if (!accepted) {
      toast.error("Debes marcar la casilla para continuar.");
      return;
    }
    const trimmed = nombre.trim();
    if (trimmed.length < 3 || trimmed.split(/\s+/).length < 2) {
      toast.error("Escribe tu nombre completo (nombre y apellido).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/acuerdo/firmar", {
        nombre_firmado: trimmed,
        version: state.version,
      });
      toast.success("Acuerdo firmado. ¡Gracias!");
      setState((s) => ({ ...s, needsSign: false }));
      await refresh();
      if (onSigned) onSigned();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.checking || !state.needsSign) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0F2942]/60 backdrop-blur-sm grid place-items-center px-4 py-6 overflow-y-auto"
      data-testid="acuerdo-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="acuerdo-gate-title"
    >
      <div className="max-w-2xl w-full rounded-xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-[#0F2942] text-white p-5 sm:p-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/10 grid place-items-center shrink-0">
            <FileSignature size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">
              Firma requerida · v{state.version}
            </div>
            <h2
              id="acuerdo-gate-title"
              className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-0.5"
            >
              Acuerdo de Uso y Consentimiento Informado
            </h2>
            <p className="text-xs text-white/80 mt-1 leading-relaxed">
              Antes de continuar necesitamos que leas y firmes el acuerdo vigente
              desde {state.fechaVigencia}. Es una firma electrónica simple, con
              pleno valor legal en México.
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          <div className="rounded-md border border-border bg-muted/40 p-4 text-sm text-foreground space-y-2">
            <div className="flex items-start gap-2">
              <ShieldCheck size={14} className="text-[#2563EB] mt-0.5 shrink-0" />
              <div>
                <b>El Servicio no es una consulta médica</b> ni sustituye la atención
                de un profesional. Es orientación educativa generada por IA sobre
                medicamentos <b>de venta libre</b>, con base en Guías de Práctica
                Clínica de México. En emergencias llama al <b>911</b>.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <BookOpen size={14} className="text-[#2563EB] mt-0.5 shrink-0" />
              <div>
                Al firmar aceptas los términos completos del acuerdo, incluyendo el
                tratamiento de datos personales sensibles de salud conforme al
                Aviso de Privacidad.
              </div>
            </div>
          </div>

          <Link
            to="/acuerdo-de-uso"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#2563EB] hover:underline text-sm"
            data-testid="acuerdo-gate-read-link"
          >
            Leer el acuerdo completo <ExternalLink size={12} />
          </Link>

          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)}
                data-testid="acuerdo-gate-checkbox"
              />
              <span className="text-sm text-foreground leading-relaxed">
                <b>He leído y acepto</b> el Acuerdo de Uso y Consentimiento
                Informado v{state.version}, y otorgo mi consentimiento expreso
                para el tratamiento de mis datos personales sensibles de salud.
              </span>
            </label>

            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Escribe tu nombre completo como firma
              </label>
              <Input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre y apellidos como aparecen en tu identificación"
                maxLength={200}
                autoComplete="name"
                data-testid="acuerdo-gate-nombre-input"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Al firmar quedará registrada la fecha, hora, IP y navegador usados
                — con valor probatorio conforme al artículo 89 del Código de
                Comercio.
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end pt-2">
            <Button
              onClick={handleFirmar}
              disabled={submitting || !accepted || !nombre.trim()}
              className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              data-testid="acuerdo-gate-firmar-btn"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Firmando…
                </>
              ) : (
                <>
                  Firmar y continuar <ArrowRight size={14} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
